import * as React from 'react';
import * as styles from './Categories.css';
import CategoryService from '../../services/CategoryService';
import { ICategoryAdmin, IItemData } from '../../interfaces/ICategoryAdmin';
import { IASpinner } from '../spinner/Spinner';
import { IAButton } from '../button/Button';
import { IASyncPanel } from './panels/syncPanel/SyncPanel';
import { IAPopup } from '../popup/Popup';
import { IAMessageBar } from '../messageBar/MessageBar';
import { LocalizationService } from '../../services/LocalizationService';
import { IInstance } from '../../interfaces/IInstance';
import { IAIcon } from '../icon/Icon';
import { IACategoryColorPanel } from './panels/categoryColorPanel/CategoryColorPanel';
import { Helper } from '../../Helper';

export interface IProps {
  environment: "Development" | "Test" | "Production";
  instance: IInstance;
  tenant: string;
  token: string;
  site: "IntraActive-Messages" | "IntraActive-Events";
  highlightColor: string;
  component: string;
  disableUnsync?: boolean;
  onChange(category: ICategoryAdmin): void;
}

export interface IState {
  openSyncCategories: boolean;
  categoryToEdit: ICategoryAdmin;
  category: ICategoryAdmin;
  isLoading: boolean;
  showUnsyncpopup: boolean;
  showWarning: string;
  categoryToEditColorsOn: IItemData;
}

export class IACategories extends React.Component<IProps, IState> {
  private readonly localizationService = new LocalizationService();

  constructor(props: IProps) {
    super(props);
    this.state = {
      category: undefined,
      categoryToEdit: undefined,
      openSyncCategories: false,
      showUnsyncpopup: false,
      isLoading: true,
      showWarning: undefined,
      categoryToEditColorsOn: undefined
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }
  
  public componentDidMount(): void {
    this.loadCategories(this.props.instance.instanceName);
  }

  public componentWillReceiveProps(props: IProps): void {
    if (props.instance?.id !== this.props.instance?.id) {
      this.loadCategories(props.instance.instanceName);
    }
  }  
  
  private loadCategories(instance: string): void {
    this.setState({ isLoading: true });
    CategoryService.getCategories(this.props.environment, instance, this.props.tenant, this.props.token, this.props.site, this.props.component).then((category) => {
      if (category.length > 0) {
        this.setState({ category: category[0] }, () => {
          this.props.onChange(this.state.category);
        });
      } else {
        this.setState({ category: null });
      }
    }).finally(() => this.setState({isLoading: false}));
  }

  private getCategoriesWithChangedColor(items: IItemData[], backgroundColor: string, textColor: string): IItemData[] {
    for (let i = 0; i < items.length; i++) {
      if (items[i].guid === this.state.categoryToEditColorsOn.guid) {
        items[i].bgColor = backgroundColor;
        items[i].textColor = textColor;
        return items;
      }
      if (items[i].children?.length > 0) {
        items[i].children = this.getCategoriesWithChangedColor(items[i].children, backgroundColor, textColor);
      }
    }
    return items;
  }

  private renderCategories(categories: IItemData[], childNumber = 0): JSX.Element{
    return <>
      {categories.map(item => {
        return <div
          className={[styles.IA_categoryParentContainer, childNumber ? styles.IA_categoryChildContainer : ""].join(" ")}
          style={{
            marginLeft: childNumber >= 1 ? 20 : 0
          }}
        >
          <div
            className={styles.IA_categoryContainer}
          >
            <div
              className={styles.IA_category}
              style={{
                cursor: "pointer",
                backgroundColor: item.bgColor ?? "#ffffff",
                color: item.textColor ?? Helper.getCategoriesDefaultTextColor()
              }}
              onClick={() => this.setState({ categoryToEditColorsOn: item })}
            >
              {item.name}
            </div>
            <IAIcon
              color={this.props.highlightColor}
              size={18}
              url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Edit/SVG/ic_fluent_edit_48_regular.svg"
              style={{
                marginLeft: 10,
                marginTop: 4
              }}
              onClick={() => this.setState({ categoryToEditColorsOn: item })}
            />
          </div>
          {item.children?.length > 0 && this.renderCategories(item.children, childNumber + 1)}
        </div>
      })}
    </>
  }

  public render(): JSX.Element {
    const categoriesIsSetUp = this.state.category && this.state.category.data && this.state.category.data.items.length > 0;
    return (
      <div
        className={styles.IA_categories}
      >
        {this.state.showUnsyncpopup &&
          <IAPopup
            headline={this.localizationService.strings.Categories_Unsync}
            content={
              <div>
                <div
                  style={{
                    float: "left",
                    fontWeight: "lighter"
                  }}
                >
                  {this.props.disableUnsync ? this.localizationService.strings.Categories_UnsyncMessageNotPossible : this.localizationService.strings.Categories_UnsyncMessage}
                </div>
                <div
                  style={{
                    clear: "both",
                    float: "left"
                  }}
                >
                  <IAButton
                    label={this.localizationService.strings.Categories_UnsyncConfirm}
                    buttonColor={"#23879b"}
                    showSpinner={false}
                    disbaled={this.props.disableUnsync}
                    borderRadius={5}
                    onClick={() => {
                      CategoryService.unsyncCategories(this.props.environment, this.props.instance.instanceName, this.props.tenant, this.props.token, this.props.site, this.props.component).then((result) => {
                        if (result.ok) {
                          this.setState({ isLoading: false, category: null, showUnsyncpopup: false });
                        } else {
                          this.setState({ showWarning: this.localizationService.strings.Categories_UnsyncWarning, isLoading: false, showUnsyncpopup: false });
                        }
                      });
                    }}
                    style={{
                      marginTop: 20,
                      float: "left"
                    }}
                  />
                  <IAButton
                    label={"Cancel"}
                    buttonColor={"#f4f4f4"}
                    borderRadius={5}
                    showSpinner={false}
                    onClick={() => {
                      this.setState({ showUnsyncpopup: false });
                    }}
                    style={{
                      marginTop: 20,
                      marginLeft: 10,
                      float: "left"
                    }}
                  />
                </div>
              </div>
            }
            close={() => {
              this.setState({ showUnsyncpopup: false });
            }}
          />
        }
        {this.state.showWarning != undefined &&
          <IAMessageBar
            type="error"
            onDismiss={() => {
              this.setState({ showWarning: undefined });
            }}
            icon="Close"
            content={this.state.showWarning}
            showIcon={true}
            showCloseButton={true}
          />
        }
        <div
          className={styles.IA_buttonArea}
        >
          {!categoriesIsSetUp ?
            <IAButton
              label={this.localizationService.strings.Categories_SetUpSubjects}
              iconUrl="https://intraactivestorage.blob.core.windows.net/cdn/icons/Settings/SVG/ic_fluent_settings_48_regular.svg"
              buttonColor={this.props.highlightColor}
              borderRadius={5}
              onClick={() => {
                this.setState({ openSyncCategories: true });
              }}
              disbaled={this.state.isLoading}
            />
          :
            <IAButton
              label={this.localizationService.strings.Categories_SyncButton}
              icon="Synchronize"
              buttonColor={this.props.highlightColor}
              borderRadius={5}
              onClick={() => {
                if (this.state.category?.data?.termGroup && this.state.category?.data?.termSet) {
                  this.setState({isLoading: true});
                  CategoryService.syncTerms(this.props.environment, this.props.instance.instanceName, this.props.tenant, this.props.token, this.state.category.data.termGroup, this.state.category.data.termSet, this.props.site, this.props.component)
                    .finally(() => this.loadCategories(this.props.instance.instanceName));
                }
              }}
              disbaled={this.state.isLoading}
            />
          }
          {categoriesIsSetUp && 
            <>
              <IAButton
                label={this.localizationService.strings.Categories_AddSubjects}
                iconUrl="https://intraactivestorage.blob.core.windows.net/cdn/icons/Open/SVG/ic_fluent_open_48_regular.svg"
                buttonColor={"#eeeeee"}
                borderRadius={5}
                onClick={() => {
                  window.open(`https://${location.host}/_layouts/15/SiteAdmin.aspx#/termStoreAdminCenter`, "_blank");
                }}
                style={{
                  marginLeft: 15,
                }}
              />
              <IAButton
                label={this.localizationService.strings.Categories_UnsyncButton}
                icon="Trash"
                buttonColor={"#c23f32"}
                borderRadius={5}
                onClick={() => {
                  this.setState({ showUnsyncpopup: true });
                }}
                style={{
                  marginLeft: 15,
                }}
                disbaled={this.state.isLoading}
              />
            </>
          }
        </div>
        <div
          className={styles.IA_syncedCategories}
        >
          {this.state.isLoading ?
            <IASpinner
              color={this.props.highlightColor}
            /> 
            :
            <>
              {categoriesIsSetUp &&
                <>
                  <p style={{marginBottom: 0}}>{this.localizationService.strings.Categories_ChangeColorDescription}</p>
                  <h2>{(this.state.category?.data?.termGroup ?? "") + (this.state.category?.data?.termSet ? ` > ${this.state.category?.data?.termSet}` : "")}</h2>
                  {this.renderCategories(this.state.category.data.items)}
                </>
              }
            </>
          }
        </div>
        <IASyncPanel
          show={this.state.openSyncCategories}
          tenant={this.props.tenant}
          token={this.props.token}
          environment={this.props.environment}
          instance={this.props.instance.instanceName}
          highlightColor={this.props.highlightColor}
          site={this.props.site}
          application={this.props.component}
          selectedTermGroup={this.state.category ? this.state.category.data.termGroup : undefined}
          selectedTermSet={this.state.category ? this.state.category.data.termSet : undefined}
          onDismiss={() => {
            this.setState({
              openSyncCategories: false
            });
          }}
          reload={() => {
            this.setState({
              openSyncCategories: false
            }, () => {
              this.loadCategories(this.props.instance.instanceName);
            });
          }}
        />
        <IACategoryColorPanel
          show={!!this.state.categoryToEditColorsOn}
          categoryToEditColors={this.state.categoryToEditColorsOn}
          highlightColor={this.props.highlightColor}
          onDismiss={() => {
            this.setState({ categoryToEditColorsOn: undefined }, () => this.loadCategories(this.props.instance.instanceName));
          }}
          onSave={(bgColor, textColor) => {
            var category = JSON.parse(JSON.stringify(this.state.category)) as ICategoryAdmin;
            category.data.items = this.getCategoriesWithChangedColor(category.data.items, bgColor, textColor);
            if (this.props.site === "IntraActive-Messages") {
              category["iaMessageCategoryData"] = category.data;
              delete category.data;
              return CategoryService.updateMessageCategories(this.props.environment, this.props.instance.instanceName, this.props.tenant, this.props.token, category);
            } else {
              return CategoryService.updateEventCategories(this.props.environment, this.props.instance.instanceName, this.props.tenant, category);
            }
          }}
        />
      </div>
    );
  }
}